import { createStore } from '@xstate/store';

export type NavStage = 'tiny' | 'small' | 'medium' | 'icons' | 'expanded';

export const NAV_STAGE_WIDTHS: Record<NavStage, number> = {
  tiny: 36,
  small: 48,
  medium: 64,
  icons: 80,
  expanded: 208,
};

export const NAV_STAGES_ORDERED: NavStage[] = ['tiny', 'small', 'medium', 'icons', 'expanded'];

const WIDTH_STORAGE_KEY = 'roadmap_2026_main_nav_width';

/** Derive rendering stage from continuous width (simple thresholds). */
export function computeStageFromWidth(width: number): NavStage {
  if (width >= 120) return 'expanded';
  if (width >= 56) return 'icons';
  if (width >= 48) return 'medium';
  if (width >= 42) return 'small';
  return 'tiny';
}

function loadWidth(): number {
  try {
    const stored = localStorage.getItem(WIDTH_STORAGE_KEY);
    if (stored) {
      const val = Number(stored);
      if (val >= NAV_STAGE_WIDTHS['tiny'] && val <= NAV_STAGE_WIDTHS['expanded']) return val;
    }
    // Migrate from old stage-based storage
    const oldStage = localStorage.getItem('roadmap_2026_main_nav_stage');
    if (oldStage && NAV_STAGES_ORDERED.includes(oldStage as NavStage)) {
      return NAV_STAGE_WIDTHS[oldStage as NavStage];
    }
  } catch {}
  return NAV_STAGE_WIDTHS['expanded'];
}

const initialWidth = loadWidth();

export const mainNavStore = createStore({
  context: {
    stage: computeStageFromWidth(initialWidth),
    rawWidth: initialWidth,
    isDragging: false,
  },
  on: {
    'nav.setStage': (_ctx, { stage }: { stage: NavStage }) => ({
      stage,
      rawWidth: NAV_STAGE_WIDTHS[stage],
      isDragging: false,
    }),
    'nav.setRawWidth': (ctx, { width }: { width: number }) => ({
      ...ctx,
      rawWidth: width,
      stage: computeStageFromWidth(width),
    }),
    'nav.startDrag': (ctx) => ({ ...ctx, isDragging: true }),
    'nav.endDrag': (ctx) => ({
      ...ctx,
      isDragging: false,
    }),
  },
});

// Persist width to localStorage
mainNavStore.subscribe((snapshot) => {
  if (!snapshot.context.isDragging) {
    try { localStorage.setItem(WIDTH_STORAGE_KEY, String(snapshot.context.rawWidth)); } catch {}
  }
});
